<template>
  <StakeModal v-model="show">
    <div class="tw-p-5 tw-flex tw-flex-col tw-items-center tw-gap-7">
      <h1 class="tw-text-xl tw-font-bold">
        Upload Recording {{ props.meeting?.id ? 'to': '' }}
      </h1>

      <div
        v-if="props.meeting?.id"
        :class="[
          'tw-flex tw-flex-col',
          'tw-border tw-border-solid tw-border-slate-300',
          'tw-rounded-lg tw-px-6 tw-p-3 tw-w-full',
          'tw-bg-slate-50'
        ]"
      >
        <span class="tw-text-xs">{{ dateString }}</span>
        <span>{{ meeting.title || 'Untitled Meeting' }}</span>
      </div>

      <Stack
        v-if="uploadComplete"
        gap="2"
      >
        <Badge
          status="info"
          icon-before="fas fa-paperclip"
        >
          {{ selectedFile?.name }}
        </Badge>

        <Callout
          status="success"
          icon-before="fas fa-check"
        >
          <p><strong>Upload Complete</strong></p>
          <p class="tw-text-sm tw-max-w-sm">
            Your recording has been uploaded and is now being processed.
            This may take a few minutes.
            It is safe to close this window.
          </p>
        </Callout>
      </Stack>

      <template v-else>
        <FileInput
          name="recording"
          accept="video/mp4,audio/mp3,audio/wav,audio/m4a,audio/x-m4a"
          variant="vertical"
          :multiple="false"
          @change="handleFileChange"
        />

        <Button
          variant="primary"
          class="tw-w-full"
          :loading="uploading"
          :disabled="!selectedFile"
          @click="handleS3Upload"
        >
          Upload
        </Button>
      </template>

      <progress
        v-if="uploading"
        class="progress is-primary"
        :value="percentComplete"
        max="100"
      >
        {{ percentComplete }}%
      </progress>
    </div>
  </StakeModal>
</template>


<script setup>
import {
  defineProps, defineEmits,
  ref,
  computed, watch,
  getCurrentInstance,
} from 'vue';

// Allows us to use $oruga
const { proxy } = getCurrentInstance();

import {
  Badge,
  Button,
  Callout,
  FileInput,
  Stack,
  StakeModal,
} from '@stakeui';

import { uploadToS3 } from '@frontend/shared/multipart-s3-uploads.js';

import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
const advisorStore = useAdvisorStore();


// Props
const props = defineProps({
  value: Boolean,
  entityExternalId: {
    type: String,
    default: null,
  },
  meeting: {
    type: Object,
    default: () => ({}),
  },
});


// Reactive state
/* eslint-disable */
const show            = ref(props.value);
const uploading       = ref(false);
const selectedFile    = ref(null);
const percentComplete = ref(0);
const uploadComplete  = ref(false);
/* eslint-enable */


// Computed props
// -------------------------------------------------------------------------------->

const meetingDate = computed(() => {
  const date = props.meeting?.meeting_date;
  return date ? new Date(date) : null;
});

const dateString = computed(() => {
  if (!meetingDate.value) return '';
  /* eslint-disable */
  return meetingDate.value.toLocaleString('en-US', {
    year:   'numeric',
    month:  'long',
    day:    'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  });
  /* eslint-enable */
});


// Events
// -------------------------------------------------------------------------------->
const emit = defineEmits(['input', 'meeting-created']);


// Watchers
// -------------------------------------------------------------------------------->

// Notify parent when modal is closed
watch(show, newValue => {
  if (!newValue) {
    if (!uploading.value) reset();
    emit('input', false);
  }
});

// Keep local modal state in sync with parent
watch(() => props.value, newValue => {
  show.value = newValue;
});


// Manage upload
// -------------------------------------------------------------------------------->

const handleS3Upload = async () => {
  try {
    // this.uploadFailed = false;
    const data = await prepareForUpload();
    if (!data) return;

    uploading.value = true;

    await uploadToS3(selectedFile.value, data.s3_key, 5, status => {
      percentComplete.value = status.percentComplete;
    });
    uploading.value = false;
    uploadComplete.value = true;

    // Now that the recording is uploaded, tell the backend to start processing the meeting.
    await startProcessing(data.meeting_id);
    setTimeout(() => {
      emit('meeting-created');
    }, 3000);
    //
  } catch (error) {
    uploading.value = false;
    console.error('Failed to upload recording to S3', error);
    danger('Unable to upload recording. Please try again or contact support.');
    throw error;
  }
};


const prepareForUpload = async () => {
  const file = selectedFile.value;
  const body = {
    file_name: file.name,
    content_type: file.type,
  };

  if (props.entityExternalId) {
    body.entity_external_id = props.entityExternalId;
  }

  if (props.meeting?.id) {
    body.meeting_id = props.meeting.id;
  }

  const res = await fetch('/api/v1/meetings/upload/prepare/', {
    method: 'POST',
    headers: advisorStore.headers,
    body: JSON.stringify(body),
  });
  const data = await res.json();

  const errorMessage = 'Unable to upload recording. Please try again or contact support.';

  if (!res.ok) {
    danger(data?.message || errorMessage);
    return [];
  }
  return data;
};


const startProcessing = async id => {
  const res = await fetch(`/api/v1/meetings/${id}/process/`, {
    method: 'GET',
    headers: advisorStore.headers,
  });
  const data = await res.json();

  if (!res.ok) {
    const errorMessage = 'Upload finished, but we were unable to process the recording. Please contact support.';
    danger(data?.message || errorMessage);
  }
};


// Other methods
// -------------------------------------------------------------------------------->

const handleFileChange = event => {
  const file = event.target.files[0];

  if (file.size > 2e9) {
    danger('File size must be less than 2GB');
    return;
  }
  selectedFile.value = file;
};


const reset = () => {
  selectedFile.value = null;
  percentComplete.value = 0;
  uploadComplete.value = false;
};


const danger = message => {
  proxy.$oruga.notification.open({
    message,
    variant: 'danger',
    position: 'top',
    duration: 5000,
  });
};

</script>


<style scoped>

</style>
